import { useRef, useState, type FormEvent } from 'react';
import { FileArchive, FileText, Link2, Paperclip, Pencil, Trash2, Upload, type LucideIcon } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { useGesteContenu, type Cours, type LeconCours, type RessourceCours } from '../../lib/useCours';
import { nombreDeRessources, tailleRessource, type NatureRessource } from '../../lib/useLecteur';

const CLASSE_ACTION =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]';
const ACTION_PRINCIPALE = `${CLASSE_ACTION} bg-codialis-navy text-white hover:bg-codialis-deep`;
const ACTION_SECONDAIRE = `${CLASSE_ACTION} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`;
const ACTION_ICONE =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10';
const CLASSE_CHAMP =
  'w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint';
const CLASSE_LIBELLE = 'mb-1 block text-xs font-semibold uppercase text-codialis-navy';

const ICONES: Record<NatureRessource, LucideIcon> = { PDF: FileText, ZIP: FileArchive, LIEN: Link2 };

type Saisie =
  | { mode: 'fichier'; fichier: File }
  | { mode: 'lien' }
  | { mode: 'modifier'; ressource: RessourceCours };

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/** « PDF · support.pdf · 2,4 Mo », « Lien · react.dev ». */
function detailRessource(ressource: RessourceCours): string {
  if (ressource.nature === 'LIEN') return `Lien · ${ressource.domaine ?? ressource.adresse ?? ''}`;
  return [ressource.nature, ressource.nomFichier, tailleRessource(ressource.tailleOctets)].filter(Boolean).join(' · ');
}

const TITRES_SAISIE: Record<Saisie['mode'], string> = {
  fichier: 'Joindre un fichier',
  lien: 'Joindre un lien',
  modifier: 'Modifier la ressource',
};

/**
 * Ressources annexes d'une leçon (TF-447), à l'étape « Contenus & supports » :
 * PDF et archives ZIP déposés, liens. L'apprenant les retrouve sous la vidéo,
 * dans l'onglet « Ressources » du lecteur, dans l'ordre d'ajout. Le format et
 * le poids sont contrôlés avant l'envoi pour éviter un envoi inutile ; l'API
 * juge, et dit ce qui bloque l'ajout (cours archivé, plafond atteint).
 */
export function RessourcesLeconCours({ cours, lecon }: { cours: Cours; lecon: LeconCours }): JSX.Element {
  const toast = useToast();
  const gestes = useGesteContenu(cours.id);
  const choixFichier = useRef<HTMLInputElement>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<Saisie | null>(null);
  const [titre, setTitre] = useState('');
  const [adresse, setAdresse] = useState('');
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  const [aRetirer, setARetirer] = useState<RessourceCours | null>(null);
  const lectureSeule = cours.refusModification !== null;
  const { limitesRessources: limites } = cours;
  const refusAjout = lecon.refusAjoutRessource;

  const ouvrir = (nouvelle: Saisie) => {
    setErreur(null);
    setErreurSaisie(null);
    setTitre(nouvelle.mode === 'modifier' ? nouvelle.ressource.titre : '');
    setAdresse(nouvelle.mode === 'modifier' ? (nouvelle.ressource.adresse ?? '') : '');
    setSaisie(nouvelle);
  };

  const fichierChoisi = (fichier: File) => {
    const point = fichier.name.lastIndexOf('.');
    const extension = point >= 0 ? fichier.name.slice(point).toLowerCase() : '';
    if (!limites.extensions.includes(extension)) {
      setErreur(`« ${fichier.name} » n’est pas accepté. Formats acceptés : ${limites.libelleFormats}.`);
      return;
    }
    if (fichier.size > limites.tailleMaxOctets) {
      setErreur(`« ${fichier.name} » est trop volumineux : ${limites.libelleTailleMax} au plus.`);
      return;
    }
    ouvrir({ mode: 'fichier', fichier });
  };

  const enregistrer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!saisie) return;
    setErreurSaisie(null);
    try {
      if (saisie.mode === 'fichier') {
        await gestes.mutateAsync({ geste: 'ajouter-fichier-ressource', leconId: lecon.id, fichier: saisie.fichier, titre: titre.trim() });
        toast('Fichier joint à la leçon');
      } else if (saisie.mode === 'lien') {
        await gestes.mutateAsync({ geste: 'ajouter-lien-ressource', leconId: lecon.id, titre: titre.trim(), adresse: adresse.trim() });
        toast('Lien joint à la leçon');
      } else {
        await gestes.mutateAsync({
          geste: 'modifier-ressource',
          leconId: lecon.id,
          ressourceId: saisie.ressource.id,
          titre: titre.trim(),
          ...(saisie.ressource.nature === 'LIEN' ? { adresse: adresse.trim() } : {}),
        });
        toast('Ressource modifiée');
      }
      setSaisie(null);
    } catch (cause) {
      setErreurSaisie(messageErreur(cause, 'La ressource n’a pas pu être enregistrée.'));
    }
  };

  const confirmerRetrait = async () => {
    const ressource = aRetirer;
    setARetirer(null);
    if (!ressource) return;
    setErreur(null);
    try {
      await gestes.mutateAsync({ geste: 'retirer-ressource', leconId: lecon.id, ressourceId: ressource.id });
      toast(ressource.nature === 'LIEN' ? 'Lien retiré' : 'Fichier retiré · effacé du serveur');
    } catch (cause) {
      setErreur(messageErreur(cause, 'La ressource n’a pas pu être retirée.'));
    }
  };

  const titreObligatoire = saisie?.mode === 'modifier';
  const adresseDemandee = saisie?.mode === 'lien' || (saisie?.mode === 'modifier' && saisie.ressource.nature === 'LIEN');
  const incomplet = (titreObligatoire && !titre.trim()) || (adresseDemandee && !adresse.trim());

  return (
    <div className="mt-2 rounded-lg bg-white p-2">
      <input
        ref={choixFichier}
        type="file"
        accept={[...limites.extensions, 'application/pdf', 'application/zip'].join(',')}
        className="hidden"
        onChange={(evenement) => {
          const fichier = evenement.target.files?.[0];
          evenement.target.value = '';
          if (fichier) fichierChoisi(fichier);
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Paperclip className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
        <div className="min-w-0 flex-1 basis-40 truncate text-xs text-codialis-navy">
          {lecon.ressources.length > 0 ? nombreDeRessources(lecon.ressources.length) : 'Aucune ressource'}
          <span className="text-codialis-gray"> · {limites.libelleFormats} ou lien</span>
        </div>
        {!lectureSeule && (
          <>
            <button
              type="button"
              disabled={refusAjout !== null || gestes.isPending}
              title={refusAjout ?? undefined}
              onClick={() => choixFichier.current?.click()}
              className={ACTION_SECONDAIRE}
              aria-label={`Joindre un fichier à « ${lecon.titre} »`}
            >
              <Upload className="h-4 w-4" aria-hidden />
              Fichier
            </button>
            <button
              type="button"
              disabled={refusAjout !== null || gestes.isPending}
              title={refusAjout ?? undefined}
              onClick={() => ouvrir({ mode: 'lien' })}
              className={ACTION_SECONDAIRE}
              aria-label={`Joindre un lien à « ${lecon.titre} »`}
            >
              <Link2 className="h-4 w-4" aria-hidden />
              Lien
            </button>
          </>
        )}
      </div>

      {lecon.ressources.length > 0 && (
        <ul className="mt-1.5 divide-y divide-gray-100">
          {lecon.ressources.map((ressource) => {
            const Icone = ICONES[ressource.nature];
            return (
              <li key={ressource.id} className="flex items-center gap-2 py-1.5 pl-1">
                <Icone className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium text-codialis-navy">{ressource.titre}</div>
                  <div className="truncate text-[11px] text-codialis-gray">{detailRessource(ressource)}</div>
                </div>
                {!lectureSeule && (
                  <>
                    <button
                      type="button"
                      disabled={gestes.isPending}
                      onClick={() => ouvrir({ mode: 'modifier', ressource })}
                      className={`${ACTION_ICONE} text-codialis-gray hover:bg-gray-100 hover:text-codialis-navy`}
                      aria-label={`Modifier « ${ressource.titre} »`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      disabled={gestes.isPending}
                      onClick={() => setARetirer(ressource)}
                      className={`${ACTION_ICONE} text-red-600 hover:bg-red-50`}
                      aria-label={`Retirer « ${ressource.titre} »`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!lectureSeule && refusAjout && <p className="mt-1 text-[11px] font-semibold text-amber-700">{refusAjout}</p>}
      {erreur && (
        <p className="mt-1 text-xs text-red-700" role="alert">
          {erreur}
        </p>
      )}

      <Modale
        titre={saisie ? TITRES_SAISIE[saisie.mode] : ''}
        sousTitre={`Leçon « ${lecon.titre} »`}
        ouverte={saisie !== null}
        onFermer={() => setSaisie(null)}
        largeur="max-w-md"
      >
        <form onSubmit={(evenement) => void enregistrer(evenement)} className="space-y-4 p-4 sm:p-6">
          {saisie?.mode === 'fichier' && (
            <p className="flex min-w-0 items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-xs text-codialis-navy">
              <FileText className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{saisie.fichier.name}</span>
              <span className="shrink-0 text-codialis-gray">{tailleRessource(saisie.fichier.size)}</span>
            </p>
          )}
          {saisie?.mode === 'modifier' && saisie.ressource.nature !== 'LIEN' && (
            <p className="text-xs text-codialis-gray">
              {detailRessource(saisie.ressource)} · pour changer de fichier, retirez celui-ci puis joignez le nouveau.
            </p>
          )}
          <div>
            <label htmlFor={`ressource-titre-${lecon.id}`} className={CLASSE_LIBELLE}>
              Titre{titreObligatoire ? '' : ' (facultatif)'}
            </label>
            <input
              id={`ressource-titre-${lecon.id}`}
              value={titre}
              maxLength={120}
              autoFocus={saisie?.mode !== 'lien'}
              placeholder={
                saisie?.mode === 'fichier'
                  ? 'Par défaut, le nom du fichier'
                  : saisie?.mode === 'lien'
                    ? 'Par défaut, le site du lien'
                    : undefined
              }
              onChange={(evenement) => setTitre(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </div>
          {adresseDemandee && (
            <div>
              <label htmlFor={`ressource-adresse-${lecon.id}`} className={CLASSE_LIBELLE}>
                Adresse du lien
              </label>
              <input
                id={`ressource-adresse-${lecon.id}`}
                type="text"
                inputMode="url"
                autoComplete="url"
                value={adresse}
                autoFocus={saisie?.mode === 'lien'}
                placeholder="https://"
                onChange={(evenement) => setAdresse(evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </div>
          )}
          {erreurSaisie && (
            <p className="text-xs text-red-700" role="alert">
              {erreurSaisie}
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setSaisie(null)} className={ACTION_SECONDAIRE}>
              Annuler
            </button>
            <button type="submit" disabled={gestes.isPending || incomplet} className={ACTION_PRINCIPALE}>
              {gestes.isPending ? 'Envoi…' : saisie?.mode === 'modifier' ? 'Enregistrer' : 'Joindre'}
            </button>
          </div>
        </form>
      </Modale>

      <Modale titre="Retirer la ressource ?" ouverte={aRetirer !== null} onFermer={() => setARetirer(null)} largeur="max-w-md">
        <div className="space-y-4 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            {aRetirer?.nature === 'LIEN'
              ? `Le lien « ${aRetirer.titre} » n’apparaîtra plus sous la leçon.`
              : `« ${aRetirer?.titre ?? ''} » est effacé du serveur : les apprenants ne pourront plus le consulter ni le télécharger.`}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setARetirer(null)} className={ACTION_SECONDAIRE}>
              Garder
            </button>
            <button
              type="button"
              onClick={() => void confirmerRetrait()}
              className={`${CLASSE_ACTION} bg-red-600 text-white hover:bg-red-700`}
            >
              Retirer
            </button>
          </div>
        </div>
      </Modale>
    </div>
  );
}
